import type { RecordLink, ToolStep } from './types.js';

export interface LiaRecordCard {
  record: RecordLink;
  step?: ToolStep;
  action: 'create' | 'update' | 'read';
}

function plain(value: string): string {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[*_`~]/g, '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr');
}

/**
 * Les cartes d'une reponse : les fiches creees ou modifiees, et parmi les fiches
 * seulement lues, celles que la reponse CITE (son texte les nomme). Les fiches
 * parcourues pour chercher restent dans le detail des outils. Une fiche = une
 * carte, quelle que soit la porte qui l'a vue ; une tache creee pour un
 * projet se range sous la carte du projet.
 */
export function liaRecordCards(steps: readonly ToolStep[], additional: readonly RecordLink[] = [], answer?: string): LiaRecordCard[] {
  const byId = new Map<string, LiaRecordCard>();
  // Une fiche « retrouvee » est celle qu'une RECHERCHE a trouvee ; les fiches relues pour
  // enrichir une reponse (le client d'un projet) restent dans le detail des outils.
  // La fiche ouverte a l'ecran, relue a la demande (« un lien pour l'ouvrir »), compte comme une fiche retrouvee.
  const searched = new Set(steps.filter((step) => (step.name === 'search_records' || step.subject === true) && step.state === 'done' && step.record).map((step) => step.record!.id));
  for (const record of additional) if (searched.has(record.id)) byId.set(record.id, { record, action: 'read' });
  for (const step of steps) {
    if (step.state !== 'done' || !step.record) continue;
    if (step.kind !== 'create' && step.kind !== 'update' && step.name !== 'search_records' && step.subject !== true && !byId.has(step.record.id)) continue;
    if (step.foldedInto) { byId.delete(step.record.id); continue; }
    const record = step.record;
    const previous = byId.get(record.id);
    const action = step.kind === 'create' || step.kind === 'update' ? step.kind : previous?.action ?? 'read';
    // Une ecriture garde sa porte ; une relecture ne remplace pas la carte d'une fiche creee ou modifiee.
    byId.set(record.id, action !== 'read' || !previous ? { record, step, action } : { ...previous, step: previous.step ?? step });
  }
  for (const step of steps) if (step.foldedInto && step.record) byId.delete(step.record.id);
  const cited = answer === undefined ? null : ` ${plain(answer).replace(/[^\p{L}\p{N}]+/gu, ' ')} `;
  const mentions = (title: string): boolean => cited === null
    || cited.includes(` ${plain(title).replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `);
  const cards = [...byId.values()];
  const reads = cards.filter((card) => card.action === 'read' && mentions(card.record.title));
  // Au-dela de deux fiches lues, la reponse est une liste ou un tableau : ses lignes s'ouvrent deja.
  const keptReads = new Set(reads.length <= MAX_READ_CARDS ? reads : []);
  return cards.filter((card) => card.action !== 'read' || keptReads.has(card));
}

const MAX_READ_CARDS = 2;

/** Une ecriture ratee puis reussie dans le meme tour n'est plus un echec a signaler ; un refus, toujours. */
export function liaUnresolvedFailure(steps: readonly ToolStep[]): boolean {
  return steps.some((step, index) => step.state === 'error' && (step.outcome === 'denied'
    || ((step.kind === 'create' || step.kind === 'update')
      && !steps.slice(index + 1).some((later) => later.kind === step.kind && later.state === 'done'))));
}

/** Toutes les fiches connues d'un tour, cartes et résultats de listes compris : de quoi rendre ouvrable une ligne citée. */
export function liaKnownRecords(steps: readonly ToolStep[], additional: readonly RecordLink[] = []): RecordLink[] {
  const byId = new Map<string, RecordLink>();
  const key = (record: RecordLink): string => `${record.appId}:${record.doorId}:${record.id}`;
  for (const step of steps) {
    if (step.state !== 'done') continue;
    for (const record of step.found ?? []) byId.set(key(record), record);
    if (step.record) byId.set(key(step.record), step.record);
  }
  for (const record of additional) byId.set(key(record), record);
  return [...byId.values()];
}

/**
 * LE RESUME DES OUTILS D'UN TOUR garde (« a lu 2 fiches · a modifie 1 fiche ») :
 * la pastille de l'en-tete d'une reponse, dans la fenetre comme dans Canaux.
 * `null` quand Lia n'a touche a rien.
 */
export function resumeDesOutilsLia(outils: { lectures: number; ecritures: number; echecs: number } | null | undefined): string | null {
  if (!outils || outils.lectures + outils.ecritures + outils.echecs === 0) return null;
  return [outils.lectures ? `a lu ${outils.lectures} ${outils.lectures > 1 ? 'fiches' : 'fiche'}` : null,
    outils.ecritures ? `a modifié ${outils.ecritures} ${outils.ecritures > 1 ? 'fiches' : 'fiche'}` : null,
    outils.echecs ? `${outils.echecs} ${outils.echecs > 1 ? 'échecs' : 'échec'}` : null].filter(Boolean).join(' · ');
}
